import React, { useEffect, useMemo, useRef, useState } from 'react';

const MONTH_LABELS = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];

function parseMonthKey(value) {
  if (!value) return null;
  const [y, m] = String(value).split('-').map(Number);
  if (!y || !m) return null;
  return { year: y, month: m };
}

/**
 * Compact month picker (prototype mpick). Only months in `availableMonths` are selectable.
 */
export default function MonthPicker({
  value,
  onChange,
  availableMonths = [],
  formatMonthDisplay,
  title = '表示月を選択',
  className = '',
}) {
  const parsed = parseMonthKey(value) || parseMonthKey(availableMonths[0]);
  const yy = parsed?.year ?? new Date().getFullYear();
  const mm = parsed?.month ?? new Date().getMonth() + 1;

  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(yy);
  const ref = useRef(null);
  const availableSet = useMemo(() => new Set(availableMonths), [availableMonths]);

  useEffect(() => {
    if (open) setViewYear(yy);
  }, [open, yy]);

  const displayLabel = value && formatMonthDisplay
    ? formatMonthDisplay(value)
    : value
      ? `${yy}年${mm}月`
      : '—';

  const pick = (m) => {
    const key = `${viewYear}-${String(m).padStart(2, '0')}`;
    if (!availableSet.has(key)) return;
    onChange(key);
    setOpen(false);
  };

  return (
    <div className={`mpick${open ? ' is-open' : ''}${className ? ` ${className}` : ''}`} ref={ref}>
      <button
        type="button"
        className={`mpick-field${open ? ' is-open' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <svg className="mpick-ic" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect x="3" y="4.5" width="18" height="16" rx="3" stroke="currentColor" strokeWidth="1.8" />
          <path d="M3 9h18M8 2.5v4M16 2.5v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
        <span className="mpick-v">{displayLabel}</span>
        <svg className="mpick-chev" viewBox="0 0 12 8" fill="none" aria-hidden="true">
          <path d="M1 1.5L6 6.5l5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <>
          <div className="mpick-scrim" onClick={() => setOpen(false)} role="presentation" />
          <div
            className="mpick-dropdown"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label={title}
          >
            <div className="mpick-sheet-title">{title}</div>
            <div className="mpick-yhead">
              <button type="button" className="mpick-ynav" onClick={() => setViewYear((v) => v - 1)} aria-label="前の年">
                ‹
              </button>
              <span className="mpick-ytitle">{viewYear}年</span>
              <button type="button" className="mpick-ynav" onClick={() => setViewYear((v) => v + 1)} aria-label="次の年">
                ›
              </button>
            </div>
            <div className="mpick-grid">
              {MONTH_LABELS.map((label, i) => {
                const m = i + 1;
                const key = `${viewYear}-${String(m).padStart(2, '0')}`;
                const enabled = availableSet.has(key);
                const sel = value === key;
                return (
                  <button
                    type="button"
                    key={m}
                    className={`mpick-m${sel ? ' is-sel' : ''}${!enabled ? ' is-disabled' : ''}`}
                    disabled={!enabled}
                    onClick={() => pick(m)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
